import React, { useState, useEffect, useCallback } from 'react';
import { useApp, ROLE_LABELS } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Save, Plus, Trash2, Calendar, IndianRupee, Shield, Settings2, Fuel
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' }, { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' }, { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const EXPENSE_ITEMS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
  { key: 'night_stay', label: 'Night Stay' },
];

const PERIODS = [
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'yearly', label: 'Yearly' },
];

export default function ExpensePoliciesTab({ businessLines, roles }) {
  const { api } = useApp();
  const [policies, setPolicies] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [selectedBL, setSelectedBL] = useState('');
  const [policyDialog, setPolicyDialog] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [policyForm, setPolicyForm] = useState({
    role: '',
    per_item_limits: { breakfast: 0, lunch: 0, dinner: 0, night_stay: 0 },
    period_limits: { weekly: 0, monthly: 0, quarterly: 0, yearly: 0 },
  });

  // Fuel Rates state
  const [fuelRates, setFuelRates] = useState([]);
  const [travelModes, setTravelModes] = useState([]);
  const [allTravelModes, setAllTravelModes] = useState([]);
  const [editingRates, setEditingRates] = useState({});
  const [newRate, setNewRate] = useState({ role_id: '', travel_mode_id: '', rate_per_km: 0 });

  const load = useCallback(async () => {
    try {
      const params = selectedBL ? { business_line_id: selectedBL } : {};
      const [polRes, cycRes, ratesRes, modesRes, allModesRes] = await Promise.all([
        api.getExpensePolicies(params),
        api.getExpenseCycles(params),
        selectedBL ? api.getFuelRates({ business_id: selectedBL }) : Promise.resolve({ data: [] }),
        selectedBL ? api.getTravelModes({ business_id: selectedBL }) : Promise.resolve({ data: [] }),
        api.getTravelModes({}),
      ]);
      setPolicies(polRes.data || []);
      setCycles(cycRes.data || []);
      setFuelRates(ratesRes.data || []);
      setTravelModes(modesRes.data || []);
      setAllTravelModes(allModesRes.data || []);
      
      // Initialize editing rates
      const initial = {};
      (ratesRes.data || []).forEach(r => { initial[r.id] = r.rate_per_km; });
      setEditingRates(initial);
    } catch (e) { console.error(e); }
  }, [api, selectedBL]);

  useEffect(() => { load(); }, [load]);

  // Auto-select first business line
  useEffect(() => {
    if (businessLines.length > 0 && !selectedBL) {
      setSelectedBL(businessLines[0].id);
    }
  }, [businessLines, selectedBL]);

  const currentCycle = cycles.find(c => c.business_line_id === selectedBL) || {};
  const blLabel = businessLines.find(b => b.id === selectedBL)?.label || '';

  const saveCycle = async (field, value) => {
    try {
      await api.upsertExpenseCycle({
        business_line_id: selectedBL,
        business_line_label: blLabel,
        ...currentCycle,
        [field]: Number(value),
      });
      toast.success('Cycle updated');
      load();
    } catch (e) { toast.error('Failed to save cycle'); }
  };

  const openPolicyDialog = (policy) => {
    if (policy) {
      setEditPolicy(policy);
      setPolicyForm({
        role: policy.role,
        per_item_limits: { ...policy.per_item_limits },
        period_limits: { ...policy.period_limits },
      });
    } else {
      setEditPolicy(null);
      setPolicyForm({
        role: '',
        per_item_limits: { breakfast: 0, lunch: 0, dinner: 0, night_stay: 0 },
        period_limits: { weekly: 0, monthly: 0, quarterly: 0, yearly: 0 },
      });
    }
    setPolicyDialog(true);
  };

  const savePolicy = async () => {
    if (!policyForm.role || !selectedBL) {
      toast.error('Select a role and business line');
      return;
    }
    try {
      const roleObj = roles.find(r => r.name === policyForm.role);
      await api.upsertExpensePolicy({
        business_line_id: selectedBL,
        business_line_label: blLabel,
        role: policyForm.role,
        role_label: roleObj?.label || ROLE_LABELS[policyForm.role] || policyForm.role,
        per_item_limits: policyForm.per_item_limits,
        period_limits: policyForm.period_limits,
      });
      toast.success('Policy saved');
      setPolicyDialog(false);
      load();
    } catch (e) { toast.error('Failed to save policy'); }
  };

  const deletePolicy = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try {
      await api.deleteExpensePolicy(id);
      toast.success('Policy deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const updateItemLimit = (field, val) => {
    setPolicyForm(prev => ({
      ...prev,
      per_item_limits: { ...prev.per_item_limits, [field]: Math.max(0, Number(val) || 0) },
    }));
  };

  const updatePeriodLimit = (field, val) => {
    setPolicyForm(prev => ({
      ...prev,
      period_limits: { ...prev.period_limits, [field]: Math.max(0, Number(val) || 0) },
    }));
  };

  // Fuel Rate handlers
  const getRoleLabel = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.label || roleId;
  };

  const getModeLabel = (modeId) => {
    let mode = travelModes.find(m => m.id === modeId);
    if (!mode) mode = allTravelModes.find(m => m.id === modeId);
    return mode?.label || modeId;
  };

  const handleSaveRate = async (rate) => {
    try {
      await api.upsertFuelRate({
        business_id: rate.business_id,
        role_id: rate.role_id,
        travel_mode_id: rate.travel_mode_id,
        rate_per_km: editingRates[rate.id] || 0
      });
      toast.success('Fuel rate saved');
      load();
    } catch (e) { toast.error('Failed to save rate'); }
  };

  const handleAddFuelRate = async () => {
    if (!newRate.role_id || !newRate.travel_mode_id) {
      toast.error('Select role and travel mode');
      return;
    }
    try {
      await api.upsertFuelRate({
        business_id: selectedBL,
        role_id: newRate.role_id,
        travel_mode_id: newRate.travel_mode_id,
        rate_per_km: newRate.rate_per_km
      });
      toast.success('Fuel rate added');
      setNewRate({ role_id: '', travel_mode_id: '', rate_per_km: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add rate'); }
  };

  const handleDeleteFuelRate = async (rateId) => {
    if (!window.confirm('Delete this fuel rate?')) return;
    try {
      await api.deleteFuelRate(rateId);
      toast.success('Fuel rate deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const existingRoles = policies.map(p => p.role);
  const availableRoles = roles.filter(r => r.name !== 'admin' && (editPolicy || !existingRoles.includes(r.name)));

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="expense-policies-tab">
      {/* Business Line Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
        <Label className="text-sm font-semibold text-slate-700 whitespace-nowrap">Business Line:</Label>
        <div className="flex gap-2 flex-wrap">
          {businessLines.map(bl => (
            <Button
              key={bl.id}
              variant={selectedBL === bl.id ? 'default' : 'outline'}
              className={`text-xs sm:text-sm ${selectedBL === bl.id ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
              size="sm"
              onClick={() => setSelectedBL(bl.id)}
            >
              {bl.label}
            </Button>
          ))}
        </div>
      </div>

      {selectedBL && (
        <>
          {/* Cycle Configuration */}
          <Card className="border border-border/60">
            <CardHeader className="pb-3 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-600" /> Cycle Config<span className="hidden sm:inline">uration</span> — {blLabel}
              </CardTitle>
              <p className="text-xs text-slate-400">Define when each period starts</p>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Weekly Starts On</Label>
                  <Select
                    value={String(currentCycle.weekly_start_day ?? 1)}
                    onValueChange={v => saveCycle('weekly_start_day', v)}
                  >
                    <SelectTrigger data-testid="cycle-weekly-start" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(d => (
                        <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Monthly Starts On Day</Label>
                  <Select
                    value={String(currentCycle.monthly_start_day ?? 1)}
                    onValueChange={v => saveCycle('monthly_start_day', v)}
                  >
                    <SelectTrigger data-testid="cycle-monthly-start" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                        <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Quarterly Starts In</Label>
                  <Select
                    value={String(currentCycle.quarterly_start_month ?? 1)}
                    onValueChange={v => saveCycle('quarterly_start_month', v)}
                  >
                    <SelectTrigger data-testid="cycle-quarterly-start" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Fiscal Year Starts In</Label>
                  <Select
                    value={String(currentCycle.yearly_start_month ?? 1)}
                    onValueChange={v => saveCycle('yearly_start_month', v)}
                  >
                    <SelectTrigger data-testid="cycle-yearly-start" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => (
                        <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role Expense Limits */}
          <Card className="border border-border/60">
            <CardHeader className="pb-3 px-3 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-amber-600" /> Expense Limits — {blLabel}
                  </CardTitle>
                  <p className="text-xs text-slate-400 mt-0.5 hidden sm:block">Set per-item and period limits for each role.</p>
                </div>
                <Button data-testid="add-policy-btn" onClick={() => openPolicyDialog(null)} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1 text-xs sm:text-sm w-full sm:w-auto">
                  <Plus className="w-4 h-4" /> Add Policy
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              {policies.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No expense policies configured. Click "Add Policy" to set limits.</p>
              ) : (
                <div className="border border-border/40 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs min-w-[600px]">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="p-2 sm:p-2.5 text-left font-semibold text-slate-700 sticky left-0 bg-slate-50">Role</th>
                        {EXPENSE_ITEMS.map(i => <th key={i.key} className="p-2 sm:p-2.5 text-center font-semibold text-slate-700 whitespace-nowrap">{i.label}</th>)}
                        <th className="p-2 sm:p-2.5 text-center font-semibold text-blue-700 border-l border-slate-200">Wk</th>
                        <th className="p-2 sm:p-2.5 text-center font-semibold text-blue-700">Mo</th>
                        <th className="p-2 sm:p-2.5 text-center font-semibold text-blue-700">Qt</th>
                        <th className="p-2 sm:p-2.5 text-center font-semibold text-blue-700">Yr</th>
                        <th className="p-2 sm:p-2.5 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {policies.map(p => (
                        <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="p-2.5">
                            <span className="font-semibold text-slate-900">{p.role_label || p.role}</span>
                          </td>
                          {EXPENSE_ITEMS.map(i => (
                            <td key={i.key} className="p-2.5 text-center">
                              {(p.per_item_limits?.[i.key] || 0) > 0
                                ? <span className="font-semibold text-slate-700">{p.per_item_limits[i.key].toLocaleString()}</span>
                                : <span className="text-slate-300">-</span>
                              }
                            </td>
                          ))}
                          {PERIODS.map(pr => (
                            <td key={pr.key} className={`p-2.5 text-center ${pr.key === 'weekly' ? 'border-l border-slate-200' : ''}`}>
                              {(p.period_limits?.[pr.key] || 0) > 0
                                ? <span className="font-semibold text-blue-600">{p.period_limits[pr.key].toLocaleString()}</span>
                                : <span className="text-slate-300">-</span>
                              }
                            </td>
                          ))}
                          <td className="p-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openPolicyDialog(p)} className="h-7 w-7 p-0">
                                <Settings2 className="w-3.5 h-3.5 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deletePolicy(p.id)} className="h-7 w-7 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fuel Rates */}
          <Card className="border border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <Fuel className="w-4 h-4 text-amber-600" /> Fuel Rates — {blLabel}
                  </CardTitle>
                  <p className="text-xs text-slate-400 mt-0.5">Set per-km fuel reimbursement rates by role and travel mode</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add new rate form */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4 p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs">Role</Label>
                  <Select value={newRate.role_id} onValueChange={v => setNewRate({ ...newRate, role_id: v })}>
                    <SelectTrigger data-testid="fuel-rate-role-select" className="mt-1">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Travel Mode</Label>
                  <Select value={newRate.travel_mode_id} onValueChange={v => setNewRate({ ...newRate, travel_mode_id: v })}>
                    <SelectTrigger data-testid="fuel-rate-mode-select" className="mt-1">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {travelModes.length > 0 ? (
                        travelModes.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)
                      ) : (
                        <SelectItem value="none" disabled>No travel modes - add in Travel Modes tab</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Rate/km (₹)</Label>
                  <Input
                    data-testid="fuel-rate-amount-input"
                    type="number"
                    min={0}
                    step={0.5}
                    value={newRate.rate_per_km}
                    onChange={e => setNewRate({ ...newRate, rate_per_km: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
                <Button data-testid="add-fuel-rate-btn" onClick={handleAddFuelRate} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1">
                  <Plus className="w-4 h-4" /> Add Rate
                </Button>
              </div>

              {fuelRates.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No fuel rates configured. Add travel modes first, then set rates for each role.</p>
              ) : (
                <div className="border border-border/40 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="p-2.5 text-left font-semibold text-slate-700">Role</th>
                        <th className="p-2.5 text-left font-semibold text-slate-700">Travel Mode</th>
                        <th className="p-2.5 text-center font-semibold text-slate-700">Rate/km (₹)</th>
                        <th className="p-2.5 text-center font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fuelRates.map(rate => (
                        <tr key={rate.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="p-2.5">
                            <Badge variant="secondary">{getRoleLabel(rate.role_id)}</Badge>
                          </td>
                          <td className="p-2.5">
                            <Badge variant="outline">{getModeLabel(rate.travel_mode_id)}</Badge>
                          </td>
                          <td className="p-2.5 text-center">
                            <Input
                              type="number"
                              min={0}
                              step={0.5}
                              value={editingRates[rate.id] ?? rate.rate_per_km}
                              onChange={e => setEditingRates({ ...editingRates, [rate.id]: parseFloat(e.target.value) || 0 })}
                              className="w-20 h-7 text-center mx-auto"
                            />
                          </td>
                          <td className="p-2.5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleSaveRate(rate)} className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50">
                                <Save className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteFuelRate(rate.id)} className="h-7 w-7 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Policy Edit Dialog */}
      <Dialog open={policyDialog} onOpenChange={setPolicyDialog}>
        <DialogContent data-testid="policy-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{editPolicy ? 'Edit' : 'Add'} Expense Policy</DialogTitle>
            <p className="text-xs text-slate-400">{blLabel} — Set per-item and period limits</p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold">Role</Label>
              <Select value={policyForm.role} onValueChange={v => setPolicyForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger data-testid="policy-role-select" className="mt-1">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {(editPolicy ? roles.filter(r => r.name !== 'admin') : availableRoles).map(r => (
                    <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-2 block">Per-Item Limits (Rs.)</Label>
              <div className="grid grid-cols-2 gap-3">
                {EXPENSE_ITEMS.map(item => (
                  <div key={item.key}>
                    <Label className="text-[10px] text-slate-500">{item.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={policyForm.per_item_limits[item.key] || ''}
                      onChange={e => updateItemLimit(item.key, e.target.value)}
                      placeholder="0 = no limit"
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-blue-700 mb-2 block">Period Limits (Rs.)</Label>
              <div className="grid grid-cols-2 gap-3">
                {PERIODS.map(p => (
                  <div key={p.key}>
                    <Label className="text-[10px] text-slate-500">{p.label}</Label>
                    <Input
                      type="number"
                      min="0"
                      value={policyForm.period_limits[p.key] || ''}
                      onChange={e => updatePeriodLimit(p.key, e.target.value)}
                      placeholder="0 = no limit"
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPolicyDialog(false)}>Cancel</Button>
            <Button data-testid="save-policy-btn" onClick={savePolicy} className="bg-amber-600 hover:bg-amber-700 text-white gap-1">
              <Save className="w-4 h-4" /> Save Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
